import MainLayout from "@/components/layout/admin/MainLayout.vue";
import store from "../../../store";
import axios from "axios";
import { required, maxLength, requiredIf } from "vuelidate/lib/validators";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import Select2Multiple from "v-select2-multiple-component";
import CONFIG from "@/assets/js/config.custom.js";
import ValidationService from "@/services/ValidationService";

export default {
  name: "DiscountTypeEdit",
  components: { MainLayout, Loading, Select2Multiple },
  mixins: [ValidationService],
  data() {
    return {
      isLoading: false,
      submitted: false,
      disabled: false,
      discountDataLoaded: false,
      campus_id: null,
      discountData: {
        id: null,
        name: "",
        discount_type: null,
        discount_amount: "",
        discount_description: "",
        is_referral_discount: false,
        discount_category: "none",
        student_ids: [],
        is_active: 1
      },
      studentOptions: [],
      noDataMsg: CONFIG.NO_DATA_FOUND
    };
  },

  computed: {
    isReferralDiscount() {
      return !!this.discountData.is_referral_discount;
    },
    requiresStudents() {
      return this.discountData.discount_category === "special"
        || this.discountData.discount_category === "lifetime";
    }
  },

  validations: {
    discountData: {
      name: { required, maxLength: maxLength(255) },
      discount_type: { required },
      discount_amount: { 
        required, 
        validValue(value) {
          if (value === null || value === undefined || value === '') return true;
          if (this.discountData.discount_type === 1) {
            return value > 0 && value <= 100;
          }
          if (this.discountData.discount_type === 2) {
            return value > 0;
          }
          return true;
        }
      },
      discount_description: { maxLength: maxLength(1000) },
      is_active: { required },
      student_ids: {
        required: requiredIf(function () {
          return this.requiresStudents;
        })
      }
    }
  },

  mounted() {
    this.campus_id = store.state.user.campus_id;
    this.isLoading = true;
    // Load students first, then discount detail
    this.loadStudents().then(() => {
      this.loadDiscountDetail();
    });
  },

  methods: {
    // Prevent non-numeric input in discount_amount
    preventKeys(e) {
      if (!/[0-9]/.test(e.key) && e.key !== "Backspace" && e.key !== "Delete") {
        e.preventDefault();
      }
    },

    showList() {
      this.$router.push({ name: "discountTypeList" });
    },

    onCategoryChange() {
      if (!this.requiresStudents) {
        this.discountData.student_ids = [];
      }
    },

    // Load student options for Select2
    loadStudents() {
      return axios.get(`/student/dropdown`, { params: { campus_id: this.campus_id } })
        .then(res => {
          this.studentOptions = Array.isArray(res.data) ? res.data : [];
        })
        .catch(err => {
          console.error("Failed to load students:", err);
        });
    },

    /**
     * Map stored flags to Discount Category radio value.
     * Lifetime implies special; otherwise special or none.
     */
    resolveDiscountCategory(isLifetime, isSpecial) {
      if (isLifetime) return "lifetime";
      if (isSpecial) return "special";
      return "none";
    },

    // Load discount detail and preselect students
    loadDiscountDetail() {
      const id = this.$route.params.id;
      if (!/^\d+$/.test(id)) {
        this.discountData = {};
        this.isLoading = false;
        return;
      }
      axios.get(`/discountType/detail/${this.$route.params.id}`)
        .then(res => {
          if (res.data) {
            const data = res.data;
            let students = [];
            if (data.student_ids) {
              if (Array.isArray(data.student_ids)) {
                students = data.student_ids.map(id => Number(id));
              } else if (typeof data.student_ids === "string") {
                students = data.student_ids
                  .split(/[\s,]+/)
                  .map(id => Number(id))
                  .filter(id => !isNaN(id));
              }
            }
            const isLifetime = !!data.is_lifetime;
            const isSpecial = !!data.is_special_discount;
            this.discountData = {
              id: data.id ?? null,
              name: data.name ?? "",
              discount_type: Number(data.discount_type),
              discount_amount: data.discount_amount,
              discount_description: data.discount_description ?? "",
              is_referral_discount: !!data.is_referral_discount,
              discount_category: this.resolveDiscountCategory(isLifetime, isSpecial),
              student_ids: students,
              is_active: Number(data.is_active)
            };
            this.discountDataLoaded = true;
          }
        })
        .catch(err => {
          console.error("Failed to load discount detail:", err);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    confirmUpdate() {
      this.submitted = true;
      this.disabled = true;
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.disabled = false;
        return;
      }
      this.updateDiscount();
    },

    updateDiscount() {
      const isSpecial = this.requiresStudents;
      const isLifetime = this.discountData.discount_category === "lifetime";

      const payload = {
        campus_id: this.campus_id,
        name: this.discountData.name,
        discount_type: this.discountData.discount_type,
        discount_amount: this.discountData.discount_amount,
        discount_description: this.discountData.discount_description,
        is_special_discount: isSpecial ? 1 : 0,
        is_lifetime: isLifetime ? 1 : 0,
        student_ids: isSpecial ? this.discountData.student_ids.map(id => Number(id)) : [],
        is_active: this.discountData.is_active,
        updated_id: store.state.user.id
      };
      axios.post(`/discountType/update/${this.discountData.id}`, payload)
        .then(() => {
          this.$notification.success("Discount Type updated successfully", { timer: 3, position: "bottomCenter" });
          this.$router.push({ name: "discountTypeList" });
        })
        .catch(err => {
          this.disabled = false;
          if (err.response?.status === 422) {
            this.printValidationError(err.response);
            if (!Object.keys(err.response.data?.errors || {}).length) {
              this.$notification.error("Please check the form errors.", {
                timer: 5,
                position: "bottomCenter"
              });
            }
            console.error("Validation Error:", err.response.data.errors);
          }else if (err.response?.status === 401) {
            store.commit("logout");
            window.location.replace("login");
          }
        });
    }
  }
};
